import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Mail, KeyRound, Phone } from "lucide-react";

export default function AdminSettings() {
  const [form, setForm] = useState({ global_markup_pct: 30, default_shipping: 9.99, vet_assignment_mode: "round_robin" });
  const [me, setMe] = useState({ email: "", alert_email: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  /* The phone Telnyx rings first when a technician presses Call. Saved per person so the
     call log shows who actually made the call, and so nobody has to install a softphone. */
  const [callCfg, setCallCfg] = useState(null);
  const [callPhone, setCallPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/settings"), api.get("/admin/me")])
      .then(([s, m]) => {
        setForm({
          global_markup_pct: s.data.global_markup_pct ?? 30,
          default_shipping: s.data.default_shipping ?? 9.99,
          vet_assignment_mode: s.data.vet_assignment_mode || "round_robin",
        });
        setMe({ email: m.data.email || "", alert_email: m.data.alert_email || m.data.email || "" });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/admin/call/config")
      .then(({ data }) => { setCallCfg(data); setCallPhone(data.my_number || ""); })
      .catch(() => setCallCfg(null));
  }, []);

  const saveCallPhone = async (e) => {
    e.preventDefault();
    setSavingPhone(true);
    try {
      const { data } = await api.post("/admin/call/my-number", { phone: callPhone });
      toast.success(`PawLinx will ring ${data.my_number_pretty}`);
      const fresh = await api.get("/admin/call/config");
      setCallCfg(fresh.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPhone(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        global_markup_pct: Number(form.global_markup_pct),
        default_shipping: Number(form.default_shipping),
        vet_assignment_mode: form.vet_assignment_mode,
      });
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (pwd.new_password && pwd.new_password !== pwd.confirm) {
      toast.error("Passwords do not match"); return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/admin/me", {
        email: me.email,
        alert_email: me.alert_email,
        ...(pwd.new_password ? { current_password: pwd.current_password, new_password: pwd.new_password } : {}),
      });
      toast.success("Profile updated");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  if (loading) return <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-3xl space-y-8" data-testid="admin-settings">
      <div>
        <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Configuration</p>
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">System Settings</h1>
        <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">Pricing defaults and admin profile.</p>
      </div>

      {/* My Profile */}
      <form onSubmit={saveProfile} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-6 space-y-5" data-testid="admin-profile-form">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
          <h2 className="font-outfit font-semibold text-lg">My Profile</h2>
        </div>

        <div className="space-y-2">
          <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Login Email</label>
          <input type="email" value={me.email} onChange={(e) => setMe({ ...me, email: e.target.value })} className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="admin-email-input" />
          <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">The email you use to log in to the admin portal.</p>
        </div>

        <div className="space-y-2">
          <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Alerts Email</label>
          <input type="email" value={me.alert_email} onChange={(e) => setMe({ ...me, alert_email: e.target.value })} className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="admin-alert-email-input" />
          <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Where AI-flagged alerts are sent. Defaults to your login email.</p>
        </div>

        <div className="border-t border-paw-border dark:border-white/5 pt-5 space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
            <p className="font-manrope text-sm text-paw-text dark:text-white/80">Change password (optional)</p>
          </div>
          <input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} placeholder="Current password" className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="admin-current-pwd" />
          <input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} placeholder="New password" className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="admin-new-pwd" />
          <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} placeholder="Confirm new password" className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="admin-confirm-pwd" />
        </div>

        <button type="submit" disabled={savingProfile} className="flex items-center gap-2 px-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm transition-colors" data-testid="admin-profile-save">
          {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Profile
        </button>
      </form>

      {/* Calling */}
      <form onSubmit={saveCallPhone} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-6 space-y-5" data-testid="admin-calling-form">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
          <h2 className="font-outfit font-semibold text-lg">Calling</h2>
        </div>
        <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/60 leading-relaxed">
          When you press Call on a refill request, PawLinx rings this phone first. Once you pick
          up it dials the clinic or the customer and joins you together. They see the PawLinx
          number on their handset, never yours. Any phone works, a mobile or a landline, and
          there is nothing to install.
        </p>
        <div className="space-y-2">
          <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">The phone PawLinx should ring</label>
          <input type="tel" value={callPhone} onChange={(e) => setCallPhone(e.target.value)} placeholder="416 555 0134"
            className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="admin-call-phone" />
          {callCfg?.from_number_pretty ? (
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">
              Calls will show as {callCfg.from_number_pretty} to the person you are calling.
            </p>
          ) : null}
        </div>
        {callCfg && !callCfg.ready && callCfg.missing?.length ? (
          <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 dark:bg-amber-400/10 p-3">
            <p className="font-manrope text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
              Calling is not switched on yet. {callCfg.missing.join(". ")}.
            </p>
          </div>
        ) : null}
        <button type="submit" disabled={savingPhone} className="flex items-center gap-2 px-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm transition-colors" data-testid="admin-call-phone-save">
          {savingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save My Number
        </button>
      </form>

      {/* Pricing defaults */}
      <form onSubmit={save} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-6 space-y-5">
        <h2 className="font-outfit font-semibold text-lg">Pricing Defaults</h2>
        <div className="space-y-2">
          <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Global Markup (%)</label>
          <input type="number" step="0.1" value={form.global_markup_pct} onChange={(e) => setForm({ ...form, global_markup_pct: e.target.value })} className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="settings-markup" />
          <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Used as fallback when a medication does not have a specific markup configured.</p>
        </div>
        <div className="space-y-2">
          <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Default Shipping ($)</label>
          <input type="number" step="0.01" value={form.default_shipping} onChange={(e) => setForm({ ...form, default_shipping: e.target.value })} className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="settings-shipping" />
        </div>
        <div className="space-y-2">
          <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Vet Assignment Mode</label>
          <select value={form.vet_assignment_mode} onChange={(e) => setForm({ ...form, vet_assignment_mode: e.target.value })} className="w-full h-11 px-4 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="settings-mode">
            <option value="round_robin">Round Robin</option>
            <option value="manual">Manual</option>
            <option value="least_loaded">Least Loaded</option>
          </select>
        </div>
        <button type="submit" disabled={saving} className="flex items-center gap-2 px-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm transition-colors" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
        </button>
      </form>
    </div>
  );
}
